import { Fragment, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from "react-redux";

import moment from 'moment-timezone';
import _ from 'lodash';

import { Dialog, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Button, Typography, Checkbox, ListItemText, Autocomplete, AutocompleteChangeReason, AutocompleteChangeDetails, Chip, FormHelperText, IconButton, Tooltip, FormControlLabel, ListSubheader } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { AccessNetwork, Action, Actions, allowsPerDeviceQuotaActions, CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE, DEVICE_TARGET_ACCESS_NETWORK, DEVICE_TARGET_PER_DEVICE_QUOTA, DATA_USAGE_TYPE_LABELS, Device, DeviceInterface, NotificationMethods, Quota, Site, StarlinkServiceLine, TrafficPolicy, User, WanProfile, WAN_SCOPE_TYPES, dataUsageTypeOfQuota, isAccessNetworkDataUsageType, isCaptiveDataUsageType, wanTypeLabel, wanTypeMatches, isFloorQuota, QuotaDirection, QUOTA_DIRECTION_CEILING, QUOTA_DIRECTION_FLOOR, FLOOR_DEFAULT_LIMIT, FLOOR_DEFAULT_RECOVERY_WINDOWS, FLOOR_DEFAULT_WARMUP_WINDOWS, FLOOR_DEFAULT_WINDOW, FLOOR_WINDOW_OPTIONS, rescaleFloorActions, windowLengthSeconds } from '../types';

import { MIN_HOUR_PERIOD, PeriodWithUnitInput, QuotaPeriodChangeEvent } from '../PeriodWithUnitInput';
import { DataWithUnitInput } from '../DataWithUnitInput';
import AlertingConditions from '../QuotaActions';
import { FloorConfigSection, RuleTypeSelector } from '../FloorQuotaConfig';
import { EscalationPreview } from '../FloorQuotaConfig/EscalationPreview';
import { clearDevicePolicyData, getAccessNetworks, getTrafficPolicies, getWanProfiles, getServiceLineForEdge } from '../slice';
import './index.css';
import { nanoid } from 'nanoid';
import { checkLoggedInUserAuthorizedToViewPage, convertDateTimeIntoTimezone, extractEmails, getTimezoneCity } from '../../../utils/util';
import { SERVICE_FUNCTION_TO_FEATURE } from '../../../config';

import teamsWebhookHelpDoc from "../../../asset/user_guide/TeamsWebhookCreateRemove.pdf";
import close from "../../../asset/image/close.png";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { MMDDYYYYHMMSS_DATE_FORMAT_24_HRS } from '../../../utils/constants';

// UI only sentinel for the WAN Types dropdown. An empty scope already means
// "count every WAN", so this option just makes that default visible and is never sent to the API.
const ALL_WAN_TYPES = '__all_wan_types__';
const ALL_WAN_TYPES_LABEL = 'All WAN Types';

// a freeSolo entry has its email as the name, so show the email once in that case
const userLabel = (user: User): string => user.name && user.name !== user.email ? user.name : user.email;

interface QuotaConfigDialogProps {
    authReducer: any;
    heading: string;
    devices: Device[];
    users: User[];
    open: boolean;
    quota: Quota | null;
    accessNetworks: AccessNetwork[];
    wanProfiles?: WanProfile[];
    trafficPolicies?: TrafficPolicy[];
    starlinkServiceLines: StarlinkServiceLine[];
    onSave: (siteId: string, deviceId: string, quota: Quota, newVal: boolean) => void;
    onCancel: () => void;
    getAccessNetworks: (params: { deviceId: string }) => void;
    getWanProfiles: (params: { deviceId: string }) => void;
    getTrafficPolicies: (params: { deviceId: string }) => void;
    getServiceLineForEdge: (params: { site_id: string, device_id: string }) => void;
    clearDevicePolicyData: () => void;
}

function QuotaConfigDialog(props: QuotaConfigDialogProps) {
    const { authReducer, heading, open, devices, users, quota, accessNetworks, wanProfiles, trafficPolicies, starlinkServiceLines, onSave, onCancel, getAccessNetworks, getWanProfiles, getTrafficPolicies, getServiceLineForEdge, clearDevicePolicyData } = props;

    const isReadOnly = quota?.access_level === "read";

    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(quota != null ? devices.find(d => d.id === quota.device_id) : undefined);
    const [deviceInterface, setDeviceInterface] = useState<string[]>(quota != null && quota?.wan_links ? quota.wan_links : []);
    const [accessNetworkIds, setAccessNetworkIds] = useState<string[]>(quota != null && quota?.access_networks ? quota.access_networks : []);
    const [accessNetworkWANScope, setAccessNetworkWANScope] = useState<string[]>(quota != null && quota?.access_network_wan_scope ? quota.access_network_wan_scope : []);
    // UI only: distinguishes 'All WAN Types' ticked from nothing ticked. Both save an empty scope.
    const [allWanTypesTicked, setAllWanTypesTicked] = useState<boolean>(_.isEmpty(quota?.access_network_wan_scope));
    const [usersToNotify, setUsersToNotify] = useState<User[]>(quota != null && quota?.users_to_notify ? quota.users_to_notify : []);
    const [quotaLimit, setQuotaLimit] = useState<number>(quota != null ? quota.limit : 107374182400);
    const [quotaPeriod, setQuotaPeriod] = useState<number>(quota != null ? quota.period : 1);
    const [quotaPeriodUnit, setQuotaPeriodUnit] = useState<string>(quota != null ? quota.period_unit : 'D');
    const [quotaStartDate, setQuotaStartDate] = useState(quota != null ? moment(new Date(moment(quota?.current_quota_start_time).valueOf())) : moment(Date.now()));
    const [notificationMethods, setNotificationMethods] = useState<string[]>(quota != null && quota.notification_methods ? quota.notification_methods : []);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [actions, setActions] = useState<Action[]>(quota != null && quota?.actions ? quota.actions.filter(ac => !ac.revert) : [])
    const [revertActions, setRevertActions] = useState<Action[]>(quota != null && quota?.actions ? quota.actions.filter(ac => ac.revert) : []);
    const [editableRevertActions, setEditableRevertActions] = useState<boolean>(false);
    const [starlinkServiceLineNumbers, setStarlinkServiceLineNumbers] = useState<string[]>(quota != null && quota?.starlink_quota?.service_line_numbers ? quota.starlink_quota.service_line_numbers : []);
    const [trackSelectedDevice, setTrackSelectedDevice] = useState(false);
    const [openTrack, setOpenTrack] = useState(false);
    const [selectedSourceType, setSelectedSourceType] = useState<string>(dataUsageTypeOfQuota(quota))
    const [direction, setDirection] = useState<QuotaDirection>(quota?.direction ?? QUOTA_DIRECTION_CEILING);
    const [recoveryWindows, setRecoveryWindows] = useState<number>(quota?.recovery_windows ?? FLOOR_DEFAULT_RECOVERY_WINDOWS);
    const [warmupWindows, setWarmupWindows] = useState<number>(quota?.warmup_windows ?? FLOOR_DEFAULT_WARMUP_WINDOWS);
    const [idleSite, setIdleSite] = useState<boolean>(!!quota?.suppression?.idle_site);
    const [sourceNote, setSourceNote] = useState<string>('');
    const [windowNote, setWindowNote] = useState<string>('');

    const isFloor = direction === QUOTA_DIRECTION_FLOOR;
    const floorWindowMinutes = isFloor ? windowLengthSeconds({ period: quotaPeriod, period_unit: quotaPeriodUnit } as Quota) / 60 : 0;
    // floors stay behind beta until released; an existing floor always opens as one
    const canChooseFloor = checkLoggedInUserAuthorizedToViewPage(SERVICE_FUNCTION_TO_FEATURE["beta-feature"]["service"], SERVICE_FUNCTION_TO_FEATURE["beta-feature"]["function"]) || isFloorQuota(quota);

    // only captive usage is metered finely enough for an hourly quota
    const allowHourlyPeriod = isCaptiveDataUsageType(selectedSourceType);

    const handleClose = (value: boolean) => {
        onCancel();
        setTrackSelectedDevice(false);
    };

    const handleSave = () => {
        let site_id = devices.filter(device => device.id === selectedDevice?.id)?.[0]?.site_id
        let device_id = selectedDevice?.id;
        let _quota: any = {
            site_id: site_id,
            device_id: device_id,
            wan_links: deviceInterface,
            access_networks: accessNetworkIds,
            captive_usage_type: CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE[selectedSourceType] ?? '',
            access_network_wan_scope: selectedSourceType === 'access_network' ? accessNetworkWANScope : [],
            starlink_quota: {
                service_line_numbers: starlinkServiceLineNumbers,
                enabled: !_.isEmpty(starlinkServiceLineNumbers)
            },
            limit: Number(quotaLimit),
            period: Number(quotaPeriod),
            period_unit: quotaPeriodUnit,
            start_time: quotaStartDate.toISOString(),
            actions: [...actions, ...revertActions],
            notification_methods: notificationMethods,
            users_to_notify: usersToNotify,
            direction: direction
        }
        if (isFloor) {
            _quota.recovery_windows = recoveryWindows;
            _quota.warmup_windows = warmupWindows;
            // nothing opted in is null, not an object of falses, matching what the API stores
            _quota.suppression = idleSite ? { idle_site: true } : null;
        }
        if (quota != null) {
            _quota.id = quota?.id;
            _quota.access_level = quota?.access_level;
            if (isReadOnly) {
                // for read-only quotas only notification fields are editable — keep them as-is
            } else {
                for (let key of ['site_id', 'device_id', 'wan_links', 'access_networks', 'captive_usage_type', 'limit', 'period', 'period_unit', 'actions', 'notification_methods', 'users_to_notify', 'direction', 'recovery_windows', 'warmup_windows', 'suppression']) {
                    if (_.isEqual(quota[key], _quota[key])) {
                        delete _quota[key];
                    }
                }
                if (_.isEqual([...(quota.access_network_wan_scope ?? [])].sort(), [...(_quota.access_network_wan_scope ?? [])].sort())) {
                    delete _quota.access_network_wan_scope;
                }
                if (quotaStartDate.isSame(moment(quota.current_quota_start_time).utc())) {
                    delete _quota.current_quota_start_time;
                }
            }
            site_id = quota?.site_id;
            device_id = quota?.device_id;
        }
        if (device_id && site_id) {
            onSave(site_id, device_id, _quota, quota == null);
        }
    };

    const handleChangeDevice = (event: SyntheticEvent<Element, Event>, value: unknown, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<unknown> | undefined) => {
        if (reason === 'selectOption') {
            let _value = value as Device;
            setSelectedDevice(_value);
        } else if (!value) {
            setSelectedDevice(undefined);
            setTrackSelectedDevice(false);
        }

        setActions([]);
        setDeviceInterface([]);
        setAccessNetworkIds([]);
        setAccessNetworkWANScope([]);
        setAllWanTypesTicked(true);
        setStarlinkServiceLineNumbers([]);
        setSelectedSourceType(isFloor ? 'interface' : '');
        setSourceNote('');
    };

    const handleChangeSource = (event: SelectChangeEvent<string[]>, child: ReactNode) => {
        const {
            target: { value },
        } = event;
        const _value = typeof value === 'string' ? value.split(',') : value;

        console.log('value', _value)

        if (child) {
            let _child = child as React.ReactElement;
            switch (_child.props['data-source-type']) {
                case 'interface':
                    if (isFloor) {
                        const added = _value.filter(di => !deviceInterface.includes(di));
                        const kept = added.length > 0 ? added.slice(-1) : _value.filter(di => deviceInterface.includes(di)).slice(0, 1);
                        setDeviceInterface(kept.filter(di => deviceInterfaces.findIndex(d => d.alias === di) !== -1));
                        setSourceNote('');
                    } else {
                        setDeviceInterface(_value.filter(di => deviceInterfaces.findIndex(d => d.alias === di) !== -1));
                    }
                    setStarlinkServiceLineNumbers([]);
                    setAccessNetworkIds([]);
                    setAccessNetworkWANScope([]);
                    setAllWanTypesTicked(true);
                    break;
                case 'access_network':
                    console.log('access_network', accessNetworks)
                    setAccessNetworkIds(_value.filter(an => accessNetworkOptions?.findIndex(a => a.id === an) !== -1));
                    setDeviceInterface([]);
                    setStarlinkServiceLineNumbers([]);
                    break;
                case 'starlink_service_line':
                    setStarlinkServiceLineNumbers(_value.filter(sl => starlinkServiceLines.findIndex(s => s.service_line_number === sl) !== -1));
                    setDeviceInterface([]);
                    setAccessNetworkIds([]);
                    setAccessNetworkWANScope([]);
                    setAllWanTypesTicked(true);
                    break;
            }
        }
    };

    const handleQuotaPeriodChange = (event: QuotaPeriodChangeEvent<HTMLInputElement>) => {
        setQuotaPeriod(event.target.value.period)
        setQuotaPeriodUnit(event.target.value.unit)
    }

    const handleQuotaLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuotaLimit(Number(event.target.value))
    }

    const handleStartDateChange = (_startDate: moment.Moment | null) => {
        if (_startDate == null || !_startDate.isValid())
            return;
        setQuotaStartDate(_startDate.seconds(0))
    }

    const handleChangeNotificationMethods = (event: React.ChangeEvent<HTMLInputElement>) => {
        let _value = [...notificationMethods];
        if (event.target.checked) {
            _value.push(event.target.name);
        } else {
            _value = _value.filter(nm => nm !== event.target.name);
        }
        setNotificationMethods(_value);
    };

    const handleChangeUsersToNotify = (event: SyntheticEvent<Element, Event>, value: unknown, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<unknown> | undefined) => {
        if (reason === 'selectOption' || reason === 'removeOption') {
            let _value = value as User[];
            setUsersToNotify(_value);
        } else if (reason === 'createOption') {
            let _users: User[] = [];
            let _value = value as (string | User)[];
            _value.forEach((v: string | User) => {
                if (typeof v === 'string') {
                    let _values: string[] = extractEmails(v)
                    _values.forEach(_v => {
                        let _user = users.find(u => u.email === _v);
                        if (_user) {
                            _users.push(_user);
                        } else {
                            _users.push({ name: _v, email: _v });
                        }
                    })
                } else {
                    _users.push(v);
                }
            });
            setUsersToNotify(_users);
        } else if (reason === 'clear') {
            setUsersToNotify([]);
        }

    };

    const handleChangeActions = (actions: Action[]) => {
        setActions(actions)
    }

    const handleChangeRevertActions = (actions: Action[]) => {
        setRevertActions(actions)
    }

    const deviceInterfaces = useMemo(() => {
        let device = devices.find(d => d.id === selectedDevice?.id);
        if (device) {
            let arr = device.interfaces;
            let key ="alias";
            return Array.from(new Map(arr.map(item =>[item[key], item])).values())
        }
        return [];
    }, [selectedDevice])

    const invalidDeviceInterfaces = useMemo(() => {
        if (isReadOnly) return []
        return deviceInterface.filter(di => deviceInterfaces.findIndex(d => d.alias === di) === -1)
    }, [deviceInterface, deviceInterfaces, isReadOnly])

    const invalidAccessNetworkIds = useMemo(() => {
        return accessNetworkIds.filter(an => accessNetworks.findIndex(a => a.id === an) === -1)
    }, [accessNetworkIds, accessNetworks])

    const invalidStarlinkServiceLineNumbers = useMemo(() => {
        return starlinkServiceLineNumbers.filter(sn => starlinkServiceLines.findIndex(s => s.service_line_number === sn) === -1)
    }, [starlinkServiceLineNumbers, starlinkServiceLines])

    const wanScopeOptions = useMemo(() => {
        // offer only the WAN types this device actually has; all of them until its interfaces load
        const onDevice: string[] = _.isEmpty(deviceInterfaces)
            ? [...WAN_SCOPE_TYPES]
            : WAN_SCOPE_TYPES.filter(wt => deviceInterfaces.some(d => wanTypeMatches(wt, d.wan_type)));
        // a saved scope keeps its types listed even if the device no longer has that WAN
        const selectedNotListed = accessNetworkWANScope.filter(wt => !onDevice.includes(wt));
        return [...onDevice, ...selectedNotListed];
    }, [accessNetworkWANScope, deviceInterfaces])

    // with 'All WAN Types' on, every type is ticked too, so MUI's own toggling can untick one of them
    const wanScopeSelectValue = allWanTypesTicked ? [ALL_WAN_TYPES, ...wanScopeOptions] : accessNetworkWANScope;

    const wanScopeTypesNotOnDevice = useMemo(() => {
        if (_.isEmpty(accessNetworkWANScope) || _.isEmpty(deviceInterfaces)) return [];
        return accessNetworkWANScope.filter(wt => deviceInterfaces.findIndex(d => wanTypeMatches(wt, d.wan_type)) === -1);
    }, [accessNetworkWANScope, deviceInterfaces])

    useEffect(() => {
        if (devices.length > 0) {
            setSelectedDevice(quota != null ? devices.find(d => d.id === quota.device_id) : undefined);
            setDeviceInterface(quota != null && quota.wan_links ? quota.wan_links : []);
            setAccessNetworkIds(quota != null && quota.access_networks ? quota.access_networks : []);
            setAccessNetworkWANScope(quota != null && quota.access_network_wan_scope ? quota.access_network_wan_scope : []);
            setAllWanTypesTicked(_.isEmpty(quota?.access_network_wan_scope));
            setStarlinkServiceLineNumbers(quota != null && quota?.starlink_quota?.service_line_numbers ? quota.starlink_quota.service_line_numbers : []);
        }
    }, [devices])

    useEffect(() => {
        if (quota == null || selectedDevice?.id !== quota?.device_id) {
            clearDevicePolicyData();
            setActions([]);
        }

        let siteId = selectedDevice?.site_id || '';
        let deviceId = selectedDevice?.id || '';
        if (!_.isEmpty(deviceId)) {
            getAccessNetworks({ deviceId: selectedDevice?.id || '' })
            getWanProfiles({ deviceId: selectedDevice?.id || '' })
            getTrafficPolicies({ deviceId: selectedDevice?.id || '' })
        }

        if (!_.isEmpty(siteId) && !_.isEmpty(deviceId)) {
            getServiceLineForEdge({ site_id: siteId, device_id: deviceId })
        }
    }, [selectedDevice])

    useEffect(() => {
        if (open) {
            if (devices.length > 0) {
                setSelectedDevice(quota != null ? devices.find(d => d.id === quota.device_id) : undefined);
                setDeviceInterface(quota != null && quota.wan_links ? quota.wan_links : []);
                setAccessNetworkIds(quota != null && quota.access_networks ? quota.access_networks : []);
                setAccessNetworkWANScope(quota != null && quota.access_network_wan_scope ? quota.access_network_wan_scope : []);
                setAllWanTypesTicked(_.isEmpty(quota?.access_network_wan_scope));
                setStarlinkServiceLineNumbers(quota != null && quota?.starlink_quota?.service_line_numbers ? quota.starlink_quota.service_line_numbers : []);
            }
            setQuotaLimit(quota != null ? quota.limit : 100000000000);
            setQuotaPeriod(quota != null ? quota.period : 1);
            setQuotaPeriodUnit(quota != null ? quota.period_unit : 'D');
            setQuotaStartDate(quota != null ? moment(new Date(moment(quota?.current_quota_start_time).valueOf())) : moment(Date.now()));
            setNotificationMethods(quota != null && quota.notification_methods ? quota.notification_methods : []);
            setUsersToNotify(quota != null && quota?.users_to_notify ? quota.users_to_notify : []);
            setActions(quota != null && quota?.actions ? quota.actions.filter(ac => !ac.revert) : [])
            setRevertActions(quota != null && quota?.actions ? quota.actions.filter(ac => ac.revert) : []);
            setSelectedSourceType(dataUsageTypeOfQuota(quota))
            setDirection(quota?.direction ?? QUOTA_DIRECTION_CEILING);
            setRecoveryWindows(quota?.recovery_windows ?? FLOOR_DEFAULT_RECOVERY_WINDOWS);
            setWarmupWindows(quota?.warmup_windows ?? FLOOR_DEFAULT_WARMUP_WINDOWS);
            setIdleSite(!!quota?.suppression?.idle_site);
            setSourceNote('');
            setWindowNote('');
        }
    }, [open, quota])

    useEffect(() => {
        const sourceValid = isReadOnly || (
            (!_.isEmpty(starlinkServiceLineNumbers) || !_.isEmpty(deviceInterface) || !_.isEmpty(accessNetworkIds)) &&
            invalidDeviceInterfaces.length === 0 &&
            invalidAccessNetworkIds.length === 0 &&
            invalidStarlinkServiceLineNumbers.length === 0 &&
            (_.isEmpty(accessNetworkWANScope) || !_.isEmpty(accessNetworkIds))
        );

        const periodValid = isFloor
            ? FLOOR_WINDOW_OPTIONS.some(o => o.period === quotaPeriod && o.unit === quotaPeriodUnit)
            : ((allowHourlyPeriod ? ['H', 'D', 'W', 'M'] : ['D', 'W', 'M']).includes(quotaPeriodUnit)) &&
              (quotaPeriodUnit !== 'H' || quotaPeriod >= MIN_HOUR_PERIOD);
        // a floor watches exactly one WAN interface (D11); the API rejects anything else
        const floorValid = !isFloor || isReadOnly || (
            selectedSourceType === 'interface' &&
            deviceInterface.length === 1 &&
            recoveryWindows >= 1 &&
            warmupWindows >= 0
        );

        const validationPasses =
            !_.isEmpty(selectedDevice) &&
            sourceValid &&
            floorValid &&
            quotaLimit > 0 &&
            quotaPeriod > 0 &&
            periodValid &&
            quotaStartDate.isValid() &&
            (actions.length > 0 ? actions.filter(a => a.valid === false).length === 0 : true) &&
            revertActions.filter(a => a.valid === false).length === 0 &&
            (isReadOnly || !notificationMethods.includes("email") || usersToNotify.length > 0) &&
            (actions.some(a => a.type === Actions.ActionNotification || a.type === Actions.ActionPredictOverageNotification) ? notificationMethods.length > 0 : true);

        let hasChanges = true;
        if (isReadOnly && quota != null) {
            const origMethods = quota.notification_methods ?? [];
            const origUsers = quota.users_to_notify ?? [];
            const origUserActions = (quota.actions ?? [])
                .filter(a => !a.revert && a.type === Actions.ActionNotification && a.usage_percentage !== 100)
                .map(a => ({ usage_percentage: a.usage_percentage }));
            const currentUserActions = actions
                .filter(a => a.type === Actions.ActionNotification && a.usage_percentage !== 100)
                .map(a => ({ usage_percentage: a.usage_percentage }));
            hasChanges =
                !_.isEqual(notificationMethods, origMethods) ||
                !_.isEqual(usersToNotify, origUsers) ||
                !_.isEqual(origUserActions, currentUserActions);
        }

        setIsValid(validationPasses && hasChanges);
    }, [selectedDevice, deviceInterface, accessNetworkIds, accessNetworkWANScope, starlinkServiceLineNumbers, quotaLimit, quotaPeriod, quotaPeriodUnit, allowHourlyPeriod, quotaStartDate, actions, notificationMethods, usersToNotify, invalidDeviceInterfaces, invalidAccessNetworkIds, invalidStarlinkServiceLineNumbers, isReadOnly, quota, revertActions, isFloor, selectedSourceType, recoveryWindows, warmupWindows])

    useEffect(() => {
        if (quota == null) {
            let _revertActions = identifyRevertActions(actions, accessNetworks, wanProfiles, trafficPolicies, isFloor);
            // identifyRevertActions mints new ids every time, which remounts every renewal row on
            // each edit. Keep the rows that describe the same revert so they stay put.
            setRevertActions(prev => {
                const kept = _revertActions.map(revertAction => prev.find(p => isSameRevertAction(p, revertAction)) ?? revertAction);
                const unchanged = kept.length === prev.length && kept.every((action, index) => action === prev[index]);
                // a device policy to restore is the one revert the operator has to fill in
                if (kept.some(a => a.type === Actions.ActionAssignDevicePolicy && !a.traffic_policy_id)) {
                    setEditableRevertActions(true);
                }
                return unchanged ? prev : kept;
            });
        }
    }, [actions])

    // The actions this quota already holds. They stay as they are: the quota has executed
    // against them, so they can be removed and replaced but not edited in place.
    const storedActionIds = useMemo(
        () => new Set((quota?.actions ?? []).map(action => action.id)),
        [quota]
    )

    const captiveAccessNetworks = useMemo(() => accessNetworks.filter(an => an.captive), [accessNetworks])

    // the captive types count one slice of a captive network's usage, so they list only
    // captive networks; 'Access Network' counts everything and still lists them all
    const accessNetworkOptions = isCaptiveDataUsageType(selectedSourceType) ? captiveAccessNetworks : accessNetworks;

    const availableSourceTypes = useMemo(() => {
        let sourceTypes: string[] = [];
        if (deviceInterfaces.length > 0) {
            sourceTypes.push('interface');
        }
        if (accessNetworks.length > 0) {
            sourceTypes.push('access_network');
        }
        if (captiveAccessNetworks.length > 0) {
            sourceTypes.push('captive_authenticated', 'captive_anonymous');
        }
        if (starlinkServiceLines.length > 0) {
            sourceTypes.push('starlink_service_line');
        }
        return sourceTypes;
    }, [deviceInterfaces, accessNetworks, captiveAccessNetworks, starlinkServiceLines])

    const showDualUsageWarning = () => {
        return starlinkServiceLineNumbers.length > 0 && deviceInterface.some(di => deviceInterfaces.find(i => i.alias == di && i.wan_type == 'starlink')) ? <Typography className='starlink-dual-message'>Warning: Double counting of starlink usage.</Typography> : null
    }

    const handleOpenInterfaces = () => {
        if (selectedDevice) {
            setOpenTrack(true);
        } else {
            setTrackSelectedDevice(true);
            setOpenTrack(false);
        }
    }

    const handleCloseInterfaces = () => {
        if (selectedDevice) {
            setOpenTrack(false);
        } else {
            setTrackSelectedDevice(false);
        }
    }

    const renderSelectedSource = (selected) => {
        if (selectedSourceType === 'interface') {
            return deviceInterfaces.filter(d => selected.indexOf(d.alias) > -1).map(d => d.alias).join(', ')
        } else if (isAccessNetworkDataUsageType(selectedSourceType)) {
            return accessNetworks.filter(a => selected.indexOf(a.id) > -1).map(a => a.name).join(', ')
        } else if (selectedSourceType === 'starlink_service_line') {
            return starlinkServiceLines.filter(s => selected.indexOf(s.service_line_number) > -1).map(s => s.nickname).join(', ')
        }
        return null;
    }

    const handleChangeSourceType = (event) => {
        setSelectedSourceType(event.target.value)
        // an authenticated quota has no unauthenticated clients for a per device policy to
        // land on, so drop those; the access network form still applies and stays
        if (!allowsPerDeviceQuotaActions(event.target.value)) {
            setActions(prev => prev.filter(action =>
                action.type !== Actions.ActionAssignDevicePolicy || action.device_target_type === DEVICE_TARGET_ACCESS_NETWORK));
            setRevertActions(prev => prev.filter(action =>
                (action.type !== Actions.ActionRemoveDevicePolicy && action.type !== Actions.ActionAssignDevicePolicy) ||
                action.device_target_type === DEVICE_TARGET_ACCESS_NETWORK));
        }
        if (!isFloor && quotaPeriodUnit === 'H' && !isCaptiveDataUsageType(event.target.value)) {
            setQuotaPeriod(1);
            setQuotaPeriodUnit('D');
        }
        setDeviceInterface([]);
        setAccessNetworkIds([]);
        setAccessNetworkWANScope([]);
        setAllWanTypesTicked(true);
        setStarlinkServiceLineNumbers([]);
    }

    const handleChangeDirection = (next: QuotaDirection) => {
        if (next === direction) return;
        setDirection(next);
        // a tier means the opposite thing in the other direction, so none carry over
        setActions([]);
        setSourceNote('');
        setWindowNote('');
        setAccessNetworkIds([]);
        setAccessNetworkWANScope([]);
        setAllWanTypesTicked(true);
        setStarlinkServiceLineNumbers([]);
        if (next === QUOTA_DIRECTION_FLOOR) {
            setQuotaPeriod(FLOOR_DEFAULT_WINDOW.period);
            setQuotaPeriodUnit(FLOOR_DEFAULT_WINDOW.unit);
            setQuotaLimit(FLOOR_DEFAULT_LIMIT);
            if (selectedSourceType !== 'interface') {
                setDeviceInterface([]);
            } else if (deviceInterface.length > 1) {
                setDeviceInterface(deviceInterface.slice(0, 1));
                setSourceNote(`Kept ${deviceInterface[0]} only: a minimum rule watches one WAN interface.`);
            }
            setSelectedSourceType(selectedDevice ? 'interface' : '');
        } else {
            setQuotaPeriod(1);
            setQuotaPeriodUnit('D');
            setQuotaLimit(100000000000);
        }
    }

    const handleChangeFloorWindow = (period: number, unit: string) => {
        const oldMinutes = floorWindowMinutes;
        const newMinutes = windowLengthSeconds({ period, period_unit: unit } as Quota) / 60;
        setQuotaPeriod(period);
        setQuotaPeriodUnit(unit);
        if (oldMinutes <= 0 || newMinutes <= 0) return;
        const rescaled = rescaleFloorActions(actions, oldMinutes, newMinutes, storedActionIds);
        setActions(rescaled.actions);
        setWindowNote(rescaled.notes.join(' '));
    }

    const handleChangeWANScope = (event: SelectChangeEvent<string[]>) => {
        const { target: { value } } = event;
        const selected = typeof value === 'string' ? value.split(',') : value;
        // 'All WAN Types' is UI only: it ticks or unticks every type, and either way the scope saves as empty
        const clicked = _.xor(wanScopeSelectValue, selected)[0];
        if (clicked === ALL_WAN_TYPES) {
            setAllWanTypesTicked(!allWanTypesTicked);
            setAccessNetworkWANScope([]);
            return;
        }
        const wanTypes = selected.filter(wanType => wanType !== ALL_WAN_TYPES);
        const everyTypeTicked = wanTypes.length === wanScopeOptions.length;
        setAllWanTypesTicked(everyTypeTicked);
        setAccessNetworkWANScope(everyTypeTicked ? [] : wanTypes);
    }

    return (
        <Dialog className="menuScroll" onClose={handleClose} open={open} maxWidth="lg" fullWidth>
            <div className="quota_management--base--margin_1x quota_management--base--font_1x">
                <div>
                    {heading}
                </div>
                <div className='margin-left-auto'>  <img
                    src={close}
                    alt='close'
                    onClick={() => { handleClose(false) }}
                    className='close-btn-ht-wd'/> </div>
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '70vh' }}>
            {!isReadOnly && canChooseFloor && <div className="quota_management--base--margin_1x">
                <Typography className="quota_management--base--pb_1x" variant="body2" color="text.secondary">
                    Rule Type
                </Typography>
                <RuleTypeSelector value={direction} locked={quota != null} onChange={handleChangeDirection} />
            </div>}
            {!isReadOnly && <div className="quota_management--base--margin_1x">
                <Typography className="quota_management--base--pb_1x" variant="body2" color="text.secondary">
                    Data Usage Source
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            id="select-device"
                            size='small'
                            value={selectedDevice}
                            onChange={handleChangeDevice}
                            options={devices}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => <TextField {...params} label="Device" variant='outlined' />}
                            renderOption={(props, option) => <li {...props} key={option.id}>{option.name}</li>}
                            placeholder='Select Device'
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size='small' className='form-parent'>
                            <InputLabel id="select-source-type-label">Data Usage Type</InputLabel>
                            <Select
                                className='select-parent'
                                labelId="select-source-type-label"
                                id="select-source-type"
                                value={selectedSourceType}
                                label="Data Usage Type"
                                onChange={handleChangeSourceType}
                            >
                                {
                                    // a floor watches exactly one WAN interface, so that is all it lists
                                    availableSourceTypes.filter(st => !isFloor || st === 'interface').map((st) => <MenuItem key={st} value={st}>{DATA_USAGE_TYPE_LABELS[st]}</MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size='small' error={invalidDeviceInterfaces?.length > 0 || invalidAccessNetworkIds?.length > 0 || invalidStarlinkServiceLineNumbers?.length > 0} className='form-parent'>
                            <InputLabel id="select-interfaces-label">Source</InputLabel>
                            <Select
                                className='select-parent'
                                labelId="select-interfaces-label"
                                id="select-interfaces"
                                multiple={true}
                                value={[...deviceInterface, ...accessNetworkIds, ...starlinkServiceLineNumbers]}
                                label="Source"
                                onChange={handleChangeSource}
                                renderValue={renderSelectedSource}
                                onOpen={handleOpenInterfaces}
                                open={openTrack}
                                onClose={handleCloseInterfaces}
                            >
                                {
                                    selectedSourceType === 'interface' && deviceInterfaces.map(d => <MenuItem dense={true} key={d.alias} value={d.alias} data-source-type={'interface'}>
                                        <Checkbox size='small' checked={deviceInterface.indexOf(d.alias) > -1} />
                                        <ListItemText primary={d.alias} />
                                    </MenuItem>)
                                }
                                {
                                    invalidDeviceInterfaces?.length > 0 ? invalidDeviceInterfaces.map(alias => <MenuItem key={alias} value={alias}>
                                        <Checkbox size='small' checked={deviceInterface.indexOf(alias) > -1} />
                                        <ListItemText primary={`${alias} <Deleted>`} />
                                    </MenuItem>) : null
                                }
                                {
                                    isAccessNetworkDataUsageType(selectedSourceType) && accessNetworkOptions.map(an => <MenuItem dense={true} key={an.id} value={an.id} data-source-type={'access_network'}>
                                        <Checkbox size='small' checked={an.id !== undefined && accessNetworkIds.indexOf(an.id) > -1} />
                                        <ListItemText primary={an.name} />
                                    </MenuItem>)
                                }
                                {
                                    invalidDeviceInterfaces?.length > 0 ? invalidDeviceInterfaces.map(alias => <MenuItem key={alias} value={alias}>
                                        <Checkbox size='small' checked={deviceInterface.indexOf(alias) > -1} />
                                        <ListItemText primary={`${alias} <Deleted>`} />
                                    </MenuItem>) : null
                                }
                                {
                                    selectedSourceType === 'starlink_service_line' && starlinkServiceLines.filter(s => s.service_line_number).map(d => <MenuItem dense={true} key={d.service_line_number} value={d.service_line_number} data-source-type={'starlink_service_line'}>
                                        <Checkbox size='small' checked={d.service_line_number !== undefined && starlinkServiceLineNumbers.indexOf(d.service_line_number) > -1} />
                                        <ListItemText primary={d.nickname} />
                                    </MenuItem>)
                                }
                                {
                                    invalidStarlinkServiceLineNumbers?.length > 0 ? invalidStarlinkServiceLineNumbers.map(alias => <MenuItem key={alias} value={alias}>
                                        <Checkbox size='small' checked={invalidStarlinkServiceLineNumbers.indexOf(alias) > -1} />
                                        <ListItemText primary={`${alias} <Deleted>`} />
                                    </MenuItem>) : null
                                }
                            </Select>
                            {
                                invalidDeviceInterfaces?.length > 0 ? <FormHelperText>Selected interface ({invalidDeviceInterfaces.join(', ')}) does not exist, please reselect the interfaces</FormHelperText> : null
                            }
                            {
                                invalidAccessNetworkIds?.length > 0 ? <FormHelperText>Selected access network ({invalidAccessNetworkIds.join(', ')}) does not exist, please reselect the access networks</FormHelperText> : null
                            }
                            {
                                invalidStarlinkServiceLineNumbers?.length > 0 ? <FormHelperText>Selected service lines ({invalidStarlinkServiceLineNumbers.join(', ')}) does not exist, please reselect the service lines</FormHelperText> : null
                            }
                            {showDualUsageWarning()}
                            {isFloor && <FormHelperText>{sourceNote}</FormHelperText>}
                        </FormControl>
                        {!selectedDevice && trackSelectedDevice ? <span className='device-length-render-text'>Please select Device</span> : null}
                    </Grid>
                    {
                        selectedSourceType === 'access_network' && <Grid item xs={12} md={4}>
                            <FormControl fullWidth size='small' className='form-parent'>
                                <InputLabel id="select-wan-scope-label">WAN Types</InputLabel>
                                <Select
                                    className='select-parent'
                                    labelId="select-wan-scope-label"
                                    id="select-wan-scope"
                                    multiple={true}
                                    value={wanScopeSelectValue}
                                    label="WAN Types"
                                    onChange={handleChangeWANScope}
                                    renderValue={() => allWanTypesTicked ? ALL_WAN_TYPES_LABEL : accessNetworkWANScope.map(wanTypeLabel).join(', ')}
                                >
                                    <MenuItem dense={true} value={ALL_WAN_TYPES}>
                                        <Checkbox size='small' checked={allWanTypesTicked} />
                                        <ListItemText primary={ALL_WAN_TYPES_LABEL} />
                                    </MenuItem>
                                    {
                                        wanScopeOptions.map(wanType => <MenuItem dense={true} key={wanType} value={wanType}>
                                            <Checkbox size='small' checked={allWanTypesTicked || accessNetworkWANScope.indexOf(wanType) > -1} />
                                            <ListItemText primary={wanTypeLabel(wanType)} />
                                        </MenuItem>)
                                    }
                                </Select>
                                <FormHelperText>
                                    {
                                        _.isEmpty(accessNetworkWANScope)
                                            ? 'Optional - Access Network and WAN Type utilized for Quota'
                                            : 'Counting only traffic that used these WAN types. Every WAN of a selected type is included.'
                                    }
                                </FormHelperText>
                                {
                                    wanScopeTypesNotOnDevice.length > 0 ? <Typography className='starlink-dual-message'>
                                        Warning: this device has no {wanScopeTypesNotOnDevice.map(wanTypeLabel).join(', ')} WAN, so that traffic counts as 0.
                                    </Typography> : null
                                }
                            </FormControl>
                        </Grid>
                    }
                </Grid>
            </div>}
            {isFloor && <div className="quota_management--base--margin_1x">
                <Typography className='quota_management--base--pb_1x' variant="body2" color="text.secondary">
                    Minimum Usage Configuration
                </Typography>
                <FloorConfigSection
                    key={`floor-${open}-${quota?.id ?? 'new'}`}
                    period={quotaPeriod}
                    periodUnit={quotaPeriodUnit}
                    limit={quotaLimit}
                    recoveryWindows={recoveryWindows}
                    warmupWindows={warmupWindows}
                    disabled={isReadOnly}
                    onChangeWindow={handleChangeFloorWindow}
                    onChangeLimit={handleQuotaLimitChange}
                    onChangeRecoveryWindows={setRecoveryWindows}
                    onChangeWarmupWindows={setWarmupWindows}
                />
            </div>}
            {!isFloor && <div className="quota_management--base--margin_1x">
                <Typography className='quota_management--base--pb_1x' variant="body2" color="text.secondary">
                    Quota Configuration
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <PeriodWithUnitInput key={`period-${allowHourlyPeriod}`} value={quotaPeriod} unit={quotaPeriodUnit} onChange={handleQuotaPeriodChange} disabled={isReadOnly} allowHourly={allowHourlyPeriod} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ minWidth: 20 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DateTimePicker
                                    label="Quota Start"
                                    value={quotaStartDate}
                                    ampm={false}
                                    timezone={getTimezoneCity(authReducer.userTimezone)}
                                    viewRenderers={{
                                        hours: renderTimeViewClock,
                                        minutes: renderTimeViewClock,
                                        seconds: renderTimeViewClock,
                                    }}
                                    onChange={handleStartDateChange}
                                    disabled={isReadOnly}
                                />
                            </LocalizationProvider>
                        </FormControl>

                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DataWithUnitInput key={`limit-${direction}`} value={quotaLimit} onChange={handleQuotaLimitChange} disabled={isReadOnly} />
                    </Grid>
                </Grid>
            </div>}
            <div className="quota_management--base--margin_1x">
                <Typography className='quota_management--base--pb_1x' variant="body2" color="text.secondary">
                    {isFloor ? 'Quota Action(s) to execute when usage stays below minimum' : 'Quota Action(s)'}
                </Typography>
                {windowNote && <FormHelperText className='quota_management--base--pb_1x'>{windowNote}</FormHelperText>}
                <AlertingConditions deviceInterfaces={deviceInterfaces} value={actions} onChange={handleChangeActions} notificationOnly={isReadOnly} allowPerDeviceQuota={!isFloor && allowsPerDeviceQuotaActions(selectedSourceType)} lockedActionIds={storedActionIds} quotaLimit={quotaLimit} floor={isFloor} windowMinutes={floorWindowMinutes} />
                {isFloor && <EscalationPreview actions={actions} windowMinutes={floorWindowMinutes} />}
            </div>
            <div className="quota_management--base--margin_1x">
                <Typography className='quota_management--base--pb_1x' variant="body2" color="text.secondary">
                    {isFloor ? 'Quota Action(s) on recovery' : 'Quota Action(s) on renewal'} {!isReadOnly && <IconButton size='small' onClick={() => { setEditableRevertActions(!editableRevertActions) }}>
                        {editableRevertActions ? <Tooltip title="Disable Edit"><EditOffIcon /></Tooltip> : <Tooltip title="Enable Edit"><EditIcon /></Tooltip>}
                    </IconButton>}
                </Typography>
                <AlertingConditions deviceInterfaces={deviceInterfaces} value={revertActions} onChange={handleChangeRevertActions} readOnly={!editableRevertActions} forRevert={true} notificationOnly={isReadOnly} allowPerDeviceQuota={!isFloor && allowsPerDeviceQuotaActions(selectedSourceType)} lockedActionIds={storedActionIds} quotaLimit={quotaLimit} floor={isFloor} windowMinutes={floorWindowMinutes} />
            </div>

            <div className="quota_management--base--margin_1x">
                <Typography className='quota_management--base--pb_1x' variant="body2" color="text.secondary">
                    Notification
                </Typography>
                <Grid spacing={2}>
                    <Grid container xs={12}>
                        <Grid item xs={12} md={2} >
                            <Checkbox size='small' name={'email'} checked={notificationMethods.includes("email")} onChange={handleChangeNotificationMethods} />
                            <Typography variant="body2" color="text.secondary" display={'inline'}>
                                Email
                            </Typography>
                        </Grid>
                        {
                            notificationMethods.includes("email") && <Grid item xs={12} md={10}>
                                <FormControl fullWidth error={!isReadOnly && usersToNotify.length === 0}>
                                    <Autocomplete
                                        id="select-users"
                                        size='small'
                                        multiple={true}
                                        className={notificationMethods.includes("email") ? "" : "disbale-user-selection"}
                                        freeSolo={true}
                                        value={usersToNotify}
                                        onChange={handleChangeUsersToNotify}
                                        options={users}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            let _option = option as User;
                                            // both parts feed the option filter, so a user is findable by name or email
                                            return userLabel(_option) === _option.email ? _option.email : `${_option.name} ${_option.email}`
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Send to (via Email)" variant='outlined' error={!isReadOnly && usersToNotify.length === 0} />}
                                        renderOption={(props, option) => <li {...props} key={nanoid()}>{userLabel(option) === option.email ? option.email : `${option.name} (${option.email})`}</li>}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Tooltip key={key} title={option.email} placement='top'>
                                                        <Chip size='small' label={userLabel(option)} {...tagProps} />
                                                    </Tooltip>
                                                );
                                            })
                                        }
                                        placeholder='Send to (via Email)'
                                    />
                                    {
                                        !isReadOnly && usersToNotify.length === 0 ? <FormHelperText>Please select at least one user/or email</FormHelperText> : null
                                    }
                                </FormControl>
                            </Grid>
                        }
                    </Grid>
                    <Grid container xs={12}>
                        <Grid item xs={12} md={2} >
                            <Checkbox size='small' name={'microsoft_teams'} checked={notificationMethods.includes("microsoft_teams")} onChange={handleChangeNotificationMethods} />
                            <Typography variant="body2" color="text.secondary" display={'inline'}>
                                Microsoft Teams
                            </Typography>
                            <IconButton size='small' onClick={() => window.open(teamsWebhookHelpDoc)}>
                                <Tooltip title="Help"><HelpOutlineIcon /></Tooltip>
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
            </div>
            <div className='quota_management--base--flex quota_management--base--margin_1x quota_management--base--flex--justify--end'>
                <Button variant="outlined" className='quota_management--button--cancel' onClick={() => { handleClose(false) }}>Cancel</Button>
                <Button variant="contained" className='quota_management--button--save' onClick={handleSave} disabled={!isValid}>Save</Button>
            </div>
        </Dialog>
    );
}

// what identifyRevertActions leaves empty is the operator's to fill, so it cannot take
// part in the match: comparing it would drop their row and put the empty one back
const sameOrUnset = (prev: string | undefined, generated: string | undefined): boolean =>
    !generated || prev === generated;

const isSameRevertAction = (prev: Action, generated: Action): boolean =>
    prev.type === generated.type &&
    sameOrUnset(prev.wan_profile_id, generated.wan_profile_id) &&
    sameOrUnset(prev.traffic_policy_id, generated.traffic_policy_id) &&
    sameOrUnset(prev.device_target, generated.device_target) &&
    prev.device_target_type === generated.device_target_type &&
    _.isEqual(prev.access_networks ?? [], generated.access_networks ?? []) &&
    _.isEqual(prev.interfaces ?? [], generated.interfaces ?? []);

const identifyRevertActions = (actions: Action[], accessNetworks: AccessNetwork[] | undefined, wanProfiles: WanProfile[] | undefined, trafficPolicies: TrafficPolicy[] | undefined, floor: boolean = false): Action[] => {

    let revertActions: Action[] = [];

    // find affected accessnetwork ids set and interfaces set
    let affectedAccessNetworkIds = new Set<string>();
    let affectedWanProfiles = new Set<string>();
    let affectedTrafficPolicies = new Set<string>();
    let affectedInterfaces = new Set<string>();
    actions.forEach(a => {
        if (a.type === Actions.ActionAssignWanProfile || a.type === Actions.ActionAssignTrafficPolicy) {
            if (a.access_networks) {
                a.access_networks.forEach((an: string) => affectedAccessNetworkIds.add(an));
            }
        }
        if (a.type === Actions.ActionDisableInterface) {
            if (a.interfaces) {
                a.interfaces.forEach((i: string) => affectedInterfaces.add(i));
            }
        }
        if (a.type === Actions.ActionAssignWanProfile || a.type === Actions.ActionAssignTrafficPolicy) {
            a?.access_networks?.forEach((an: string) => {
                let _an = accessNetworks?.find(a => a.id === an);
                if (_an) {
                    if (a.type === Actions.ActionAssignWanProfile) {
                        affectedWanProfiles.add(_an.wan_profile);
                    } else if (a.type === Actions.ActionAssignTrafficPolicy) {
                        affectedTrafficPolicies.add(_an.vlan_aggregate);
                    }
                }
            });
        }
    });

    // create map of wan profile to array of access networks, map of traffic policy to array of access networks
    let wanProfileToAccessNetworks = new Map<string, string[]>();
    let trafficPolicyToAccessNetworks = new Map<string, string[]>();
    if (accessNetworks) {
        accessNetworks.forEach(an => {
            if (affectedWanProfiles.has(an.wan_profile)) {
                if (wanProfileToAccessNetworks.has(an.wan_profile)) {
                    wanProfileToAccessNetworks.get(an.wan_profile)?.push(an.id);
                } else {
                    wanProfileToAccessNetworks.set(an.wan_profile, [an.id]);
                }
            }
            if (affectedTrafficPolicies.has(an.vlan_aggregate)) {
                if (trafficPolicyToAccessNetworks.has(an.vlan_aggregate)) {
                    trafficPolicyToAccessNetworks.get(an.vlan_aggregate)?.push(an.id);
                } else {
                    trafficPolicyToAccessNetworks.set(an.vlan_aggregate, [an.id]);
                }
            }
        });
    }
    // create actions to assign wan profile
    wanProfileToAccessNetworks.forEach((ans, wp) => {
        revertActions.push({
            id: nanoid(),
            usage_percentage: 0,
            type: Actions.ActionAssignWanProfile,
            access_networks: ans,
            wan_profile_id: wp,
            executed: false,
            revert: true
        });
    });

    // create actions to assign traffic policy
    trafficPolicyToAccessNetworks.forEach((ans, tp) => {
        revertActions.push({
            id: nanoid(),
            usage_percentage: 0,
            type: Actions.ActionAssignTrafficPolicy,
            access_networks: ans,
            traffic_policy_id: tp,
            executed: false,
            revert: true
        });
    });

    // A device policy assignment leaves the policy on the client until something takes
    // it off, so it gets a removal. It names no client here — the quota records what it
    // assigned when it fires, and the removal reads that back.
    // a floor has no per device assignment and the API refuses its removal there
    const assignedByQuota = !floor && actions.some(a => a.type === Actions.ActionAssignDevicePolicy && a.device_target_type === DEVICE_TARGET_PER_DEVICE_QUOTA);
    if (assignedByQuota) {
        revertActions.push({
            id: nanoid(),
            usage_percentage: 0,
            type: Actions.ActionRemoveDevicePolicy,
            device_target_type: DEVICE_TARGET_PER_DEVICE_QUOTA,
            executed: false,
            revert: true
        });
    }
    // An access network keeps whatever device policy it already had, and nothing records
    // what that was, so renewal cannot simply clear it — it assigns a policy back. The
    // operator says which, and the empty one below holds the row invalid until they do.
    const devicePolicyNetworks = new Set<string>();
    actions.forEach(a => {
        if (a.type === Actions.ActionAssignDevicePolicy && a.device_target_type === DEVICE_TARGET_ACCESS_NETWORK) {
            a.access_networks?.forEach((an: string) => devicePolicyNetworks.add(an));
        }
    });
    // one restore per network, matching the one network each assignment carries
    devicePolicyNetworks.forEach(accessNetwork => {
        revertActions.push({
            id: nanoid(),
            usage_percentage: 0,
            type: Actions.ActionAssignDevicePolicy,
            device_target_type: DEVICE_TARGET_ACCESS_NETWORK,
            access_networks: [accessNetwork],
            traffic_policy_id: '',
            executed: false,
            revert: true
        });
    });
    // create actions to enable interfaces
    if (affectedInterfaces.size > 0) {
        revertActions.push({
            id: nanoid(),
            usage_percentage: 0,
            type: Actions.ActionEnableInterfaces,
            interfaces: Array.from(affectedInterfaces),
            executed: false,
            revert: true
        });
    }

    return revertActions;
}

const mapStateToProps = (state) => ({
    authReducer: state.authReducer,
    accessNetworks: state.quotaManager.accessNetworks,
    wanProfiles: state.quotaManager.wanProfiles,
    trafficPolicies: state.quotaManager.trafficPolicies,
    starlinkServiceLines: state.quotaManager.starlinkServiceLines
});

export default connect(mapStateToProps, { getAccessNetworks, getWanProfiles, getTrafficPolicies, getServiceLineForEdge, clearDevicePolicyData })(QuotaConfigDialog);